import { Component, Input, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ReportsApiService } from '../reports.apiservice';
import { IRollupReportDataResponse } from '../../../core/models/reports.models';

@Component({
  selector: 'app-reports-rollup-report-view',
  templateUrl: './reports-rollup-report-view.component.html',
  styleUrls: ['./reports-rollup-report-view.component.scss']
})
export class ReportsRollupReportViewComponent implements OnInit {
  @Input() iRollupReportDataResponse: IRollupReportDataResponse | null = null;
  @Input() iRawReports: Array<any> | null = null;

  readonly conversionActionOptions = [
    { key: 'leads', labelKey: 'LEADS', aliases: ['leads'] },
    { key: 'purchases', labelKey: 'PURCHASES', aliases: ['purchases'] },
    { key: 'add_to_cart', labelKey: 'ADD_TO_CART', aliases: ['add_to_cart'] },
    { key: 'checkout', labelKey: 'CHECKOUT', aliases: ['checkout', 'initiate_checkout'] },
    { key: 'booking', labelKey: 'BOOKING', aliases: ['booking', 'schedule'] },
    { key: 'calls', labelKey: 'CALLS', aliases: ['calls'] },
    { key: 'book', labelKey: 'BOOK', aliases: ['book'] },
    { key: 'show', labelKey: 'SHOW', aliases: ['show'] },
    { key: 'enroll', labelKey: 'ENROLL', aliases: ['enroll'] },
    { key: 'revenue', labelKey: 'REVENUE', aliases: ['revenue'] },
    { key: 'trial_started', labelKey: 'TRIAL_STARTED', aliases: ['trial_started', 'start_trial'] },
    { key: 'subscription', labelKey: 'SUBSCRIPTION', aliases: ['subscription', 'subscribe'] }
  ];

  loadCompleted = false;
  reportModels: Array<any> = [];
  activePlatform = '';
  conversionActionBreakdownByPlatform: Record<string, Array<{ key: string; labelKey: string; value: number }>> = {
    meta: [],
    google: []
  };
  selectedConversionActionsByPlatform: Record<string, string[]> = { meta: [], google: [] };

  get combinedSummary() {
    return this.buildSummary(this.reportModels);
  }

  get metaSummary() {
    return this.buildSummary(this.reportModels.filter(report => this.platformKey(report.Platform) === 'meta'));
  }

  get googleSummary() {
    return this.buildSummary(this.reportModels.filter(report => this.platformKey(report.Platform) === 'google'));
  }

  constructor(private reportsService: ReportsApiService) {}

  ngOnInit(): void {
    const rollup = this.iRollupReportDataResponse;
    const campaigns = rollup?.campaigns || [];
    if (!rollup?.orderId || campaigns.length === 0) {
      this.loadCompleted = true;
      return;
    }

    if (Array.isArray(this.iRawReports)) {
      this.setReportModels(this.iRawReports);
      return;
    }

    forkJoin(campaigns.map(campaign => this.reportsService
      .getRawCampaignsData(
        campaign.id,
        this.apiPlatform(campaign.platform),
        rollup.dateStart,
        rollup.dateStop,
        '4'
      )
      .pipe(catchError(() => of(null)))
    )).subscribe(reports => {
      this.setReportModels(reports);
    });
  }

  private setReportModels(reports: Array<any>): void {
    this.reportModels = this.aggregateReports(reports.filter(report => !!report));
    this.initializeConversionActions(reports);
    this.activePlatform = this.reportModels.some(report => this.platformKey(report.Platform) === 'meta')
      ? 'meta'
      : this.reportModels.length > 0 ? 'google' : '';
    this.loadCompleted = true;
  }

  private aggregateReports(reports: Array<any>): Array<any> {
    const grouped = new Map<string, any>();

    const faultedCampaigns = (this.iRollupReportDataResponse?.campaigns || [])
      .filter(campaign => campaign.bundle === 'Faulted' ||
        this.iRollupReportDataResponse?.campaignDataStatus?.some(status =>
          status.campaignId === campaign.id && status.status === 'Faulted'));

    // Keep faulted campaigns visible even though they have no report model to aggregate.
    reports = [...reports, ...faultedCampaigns.map(campaign => ({
      Platform: this.normalizePlatform(campaign.platform),
      CurrencyCode: 'USD',
      DaySplitsAdsPdfModels: [{
        CampaignName: campaign.name,
        NoDataForReportingPeriod: true
      }],
      IsFaultedCampaign: true
    }))];

    for (const report of reports) {
      if (!report || report.ErrorMessage || !report.Platform) {
        continue;
      }

      const platform = this.normalizePlatform(report.Platform);
      let aggregate = grouped.get(platform);
      if (!aggregate) {
          aggregate = {
          Platform: platform,
          CampaignName: `${this.iRollupReportDataResponse?.company?.name || ''}`.trim(),
          GraphDateRange: report.GraphDateRange,
          StartDate: this.iRollupReportDataResponse?.dateStart,
          StopDate: this.iRollupReportDataResponse?.dateStop,
          CurrencyCode: report.CurrencyCode,
          DaySplitsAdsPdfModels: [],
          GoogleKeywords: [],
          GoogleDevices: [],
          FacebookTopFives: [],
          FacebookDemographics: [],
          FacebookDevices: [],
          Dates: [],
          Data: []
        };
        grouped.set(platform, aggregate);
      }

      for (const field of ['Clicks', 'Impressions', 'Conversions', 'Cost']) {
        aggregate[field] = (aggregate[field] || 0) + Number(report[field] || 0);
      }

      aggregate.DaySplitsAdsPdfModels.push(...(report.DaySplitsAdsPdfModels || []));
      aggregate.GoogleKeywords.push(...(report.GoogleKeywords || []));
      aggregate.GoogleDevices.push(...(report.GoogleDevices || []));
      aggregate.FacebookTopFives.push(...(report.FacebookTopFives || []));
      aggregate.FacebookDemographics.push(...(report.FacebookDemographics || []));
      aggregate.FacebookDevices.push(...(report.FacebookDevices || []));
      this.mergeDailyData(aggregate, report);
      this.mergeConversionActions(aggregate, report);
    }

    return Array.from(grouped.values()).map(aggregate => {
      aggregate.DaySplitsAdsPdfModels = this.filterCampaignRows(aggregate.DaySplitsAdsPdfModels);
      const daily = Array.from(aggregate.dailyData.entries())
        .sort((a: any, b: any) => a[0].localeCompare(b[0]));
      aggregate.Dates = daily.map((item: any) => item[0]);
      aggregate.Data = daily.map((item: any) => item[1]);
      delete aggregate.dailyData;
      return aggregate;
    });
  }

  private filterCampaignRows(models: Array<any>): Array<any> {
    const emptyModels = models.filter(model => model?.NoDataForReportingPeriod);
    const populatedNames = models
      .filter(model => !model?.NoDataForReportingPeriod)
      .map(model => this.campaignNameKey(model?.CampaignName));

    return models.filter(model => {
      if (!model?.NoDataForReportingPeriod) {
        return true;
      }

      const name = this.campaignNameKey(model.CampaignName);
      const hasPopulatedVariant = populatedNames.some(populatedName =>
        populatedName === name || populatedName.startsWith(`${name} `));

      if (hasPopulatedVariant) {
        return false;
      }

      return !emptyModels.some(other => {
        if (other === model) {
          return false;
        }

        const otherName = this.campaignNameKey(other?.CampaignName);
        return otherName.length > 0
          && otherName.length < name.length
          && name.startsWith(`${otherName} `);
      });
    });
  }

  private campaignNameKey(name: any): string {
    return (name ?? '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
  }

  private mergeDailyData(aggregate: any, report: any): void {
    if (!aggregate.dailyData) {
      aggregate.dailyData = new Map<string, number>();
    }

    const dates = report.Dates || [];
    const values = report.Data || [];
    dates.forEach((date: string, index: number) => {
      aggregate.dailyData.set(date, (aggregate.dailyData.get(date) || 0) + Number(values[index] || 0));
    });
  }

  private mergeConversionActions(aggregate: any, report: any): void {
    if (!aggregate.ConversionActions) {
      aggregate.ConversionActions = {};
    }

    for (const option of this.conversionActionOptions) {
      const value = this.getConversionActionValue(report, option.aliases);
      if (value > 0) {
        aggregate.ConversionActions[option.key] = (aggregate.ConversionActions[option.key] || 0) + value;
      }
    }
  }

  private initializeConversionActions(reports: Array<any>): void {
    this.conversionActionBreakdownByPlatform = { meta: [], google: [] };
    this.selectedConversionActionsByPlatform = { meta: [], google: [] };
    const validReports = reports.filter(report => report && !report.ErrorMessage && report.Platform);
    const campaignReportsWithMetrics = validReports
      .filter(report => ['meta', 'google'].includes(this.platformKey(report.Platform)))
      .filter(report => this.hasReportMetrics(report));
    const qualifyingCampaigns = campaignReportsWithMetrics
      .filter(report => this.isLeadCampaignCandidate(report)).length;
    const defaultsToLeadsOnly = campaignReportsWithMetrics.length > 0 &&
      qualifyingCampaigns >= campaignReportsWithMetrics.length / 2;

    for (const platform of ['meta', 'google']) {
      const platformReports = this.reportModels.filter(report => this.platformKey(report.Platform) === platform);
      let actions = this.conversionActionOptions
        .map(option => ({
          key: option.key,
          labelKey: option.labelKey,
          value: platformReports.reduce(
            (sum, report) => sum + this.getConversionActionValue(report, option.aliases),
            0
          )
        }))
        .filter(action => action.value > 0);

      if (defaultsToLeadsOnly && !actions.some(action => action.key === 'leads')) {
        actions = [{ key: 'leads', labelKey: 'LEADS', value: 0 }, ...actions];
      }

      this.conversionActionBreakdownByPlatform[platform] = actions;
      this.selectedConversionActionsByPlatform[platform] = defaultsToLeadsOnly
        ? ['leads']
        : actions.map(action => action.key);
    }
  }

  private hasReportMetrics(report: any): boolean {
    const campaignRows = report.DaySplitsAdsPdfModels;
    if (Array.isArray(campaignRows) && campaignRows.length > 0) {
      return campaignRows.some((row: any) => !row?.NoDataForReportingPeriod);
    }

    return ['Clicks', 'Impressions', 'Conversions', 'Cost']
      .some(field => Number(report[field] || 0) > 0) ||
      this.conversionActionOptions.some(option => this.getConversionActionValue(report, option.aliases) > 0);
  }

  private isLeadCampaignCandidate(report: any): boolean {
    const platformName = (report.PlatformCampaignName ?? '').toString().trim();
    const leadCampaignTokens = new Set([
      'lead',
      'leads',
      'landingpage',
      'lptraffic',
      'websitetraffic',
      'micrositeleads',
      'traffic'
    ]);

    return platformName.split('_').some((part: string) =>
      leadCampaignTokens.has(part.split('-', 1)[0].trim().toLowerCase())
    );
  }

  private getConversionActionValue(report: any, aliases: string[]): number {
    const source = report?.ConversionActions || report?.ConversionActionBreakdown ||
      report?.ActionsBreakdown || report?.ActionBreakdown || report?.Actions;
    if (!source || typeof source !== 'object') {
      return 0;
    }

    const acceptedKeys = new Set(aliases.map(alias => this.normalizeActionKey(alias)));
    const entries: Array<[string, any]> = Array.isArray(source)
      ? source.map(item => [item?.key ?? item?.label ?? item?.name, item?.value ?? item?.count])
      : Object.entries(source);
    const values = entries
      .filter(([key]) => acceptedKeys.has(this.normalizeActionKey(key)))
      .map(([, rawValue]) => Number(rawValue?.value ?? rawValue?.count ?? rawValue))
      .filter(value => Number.isFinite(value));

    return values.length > 0 ? Math.max(...values) : 0;
  }

  private normalizeActionKey(key: any): string {
    return (key ?? '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  getConversionActionsForPlatform(platform: string): Array<{ key: string; labelKey: string; value: number }> {
    return this.conversionActionBreakdownByPlatform[platform] || [];
  }

  hasMultipleConversionActionsForPlatform(platform: string): boolean {
    return this.getConversionActionsForPlatform(platform).length > 1;
  }

  toggleSelectedConversion(platform: string, actionKey: string): void {
    const selectedActions = this.selectedConversionActionsByPlatform[platform] || [];
    const nextSelection = selectedActions.includes(actionKey)
      ? selectedActions.filter(key => key !== actionKey)
      : [...selectedActions, actionKey];
    this.selectedConversionActionsByPlatform = {
      ...this.selectedConversionActionsByPlatform,
      [platform]: nextSelection
    };
  }

  private getSelectedConversionCount(report: any): number {
    const platform = this.platformKey(report.Platform);
    const actions = this.getConversionActionsForPlatform(platform);
    if (actions.length === 0) {
      return Number(report.Conversions || 0);
    }

    return (this.selectedConversionActionsByPlatform[platform] || []).reduce((sum, actionKey) => {
      const option = this.conversionActionOptions.find(item => item.key === actionKey);
      return sum + (option ? this.getConversionActionValue(report, option.aliases) : 0);
    }, 0);
  }

  private normalizePlatform(platform: string): string {
    return platform?.trim().toLowerCase() === 'facebook' ? 'Meta' : platform;
  }

  platformKey(platform: string): string {
    return platform?.trim().toLowerCase() === 'facebook' || platform?.trim().toLowerCase() === 'meta'
      ? 'meta'
      : 'google';
  }

  get hasMetaPlatform(): boolean {
    return this.reportModels.some(report => this.platformKey(report.Platform) === 'meta');
  }

  get hasGooglePlatform(): boolean {
    return this.reportModels.some(report => this.platformKey(report.Platform) === 'google');
  }

  get isLeadOnlySelection(): boolean {
    const platforms = Array.from(new Set(this.reportModels.map(report => this.platformKey(report.Platform))));
    return platforms.length > 0 && platforms.every(platform => {
      const availableActions = this.getConversionActionsForPlatform(platform);
      if (availableActions.length === 0) {
        return true;
      }

      const selectedActions = this.selectedConversionActionsByPlatform[platform] || [];
      return selectedActions.length === 1 && selectedActions[0] === 'leads';
    });
  }

  private buildSummary(reports: Array<any>) {
    const summary = reports.reduce((result, report) => {
      result.spend += Number(report.Cost || 0);
      result.clicks += Number(report.Clicks || 0);
      result.impressions += Number(report.Impressions || 0);
      result.leads += this.getSelectedConversionCount(report);
      result.campaigns += new Set((report.DaySplitsAdsPdfModels || [])
        .filter((model: any) => !model.NoDataForReportingPeriod)
        .map((model: any) => model.CampaignName || '')).size;
      return result;
    }, { campaigns: 0, spend: 0, clicks: 0, impressions: 0, leads: 0 });

    summary.ctr = summary.impressions > 0 ? summary.clicks / summary.impressions : 0;
    summary.cpc = summary.clicks > 0 ? summary.spend / summary.clicks : 0;
    summary.cpm = summary.impressions > 0 ? (summary.spend / summary.impressions) * 1000 : 0;
    summary.cpl = summary.leads > 0 ? summary.spend / summary.leads : 0;
    summary.lsr = summary.clicks > 0 ? summary.leads / summary.clicks : 0;
    return summary;
  }

  private apiPlatform(platform: string): string {
    return platform?.trim().toLowerCase() === 'meta' ? 'Facebook' : platform;
  }
}
